import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const cancelled = params.get("cancelled") === "1";
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ loading: true });

  useEffect(() => {
    if (cancelled || !sessionId) { setStatus({ loading: false, cancelled: true }); return; }
    let stopped = false;
    let attempts = 0;
    const poll = async () => {
      if (stopped) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus({ loading: false, paid: true, data }); return; }
        if (attempts++ >= 15) { setStatus({ loading: false, timeout: true }); return; }
        setTimeout(poll, 2000);
      } catch { setStatus({ loading: false, error: true }); }
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId, cancelled]);

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Navbar variant="adult" />
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        {status.loading && (
          <>
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-amber-400" />
            <div className="mt-6 font-adult-heading text-2xl">Confirming your payment…</div>
            <div className="mt-2 text-zinc-400 text-sm">Hang tight — this usually takes a few seconds.</div>
          </>
        )}
        {status.paid && <PaidState data={status.data} />}
        {status.cancelled && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center">
              <XCircle className="w-8 h-8 text-zinc-400" />
            </div>
            <h1 className="mt-6 font-adult-heading text-3xl font-semibold tracking-tight">Payment cancelled</h1>
            <div className="mt-2 text-zinc-400">No worries — nothing was charged. Try again whenever you&apos;re ready.</div>
            <Link to="/courses" className="inline-block mt-6 rounded-full border border-zinc-800 hover:border-zinc-600 px-6 py-3 text-sm">Back to courses</Link>
          </>
        )}
        {(status.timeout || status.error) && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-yellow-400" />
            </div>
            <h1 className="mt-6 font-adult-heading text-3xl font-semibold tracking-tight">Still processing</h1>
            <div className="mt-2 text-zinc-400">Your payment is being finalised. Check your dashboard in a minute.</div>
            <Link to="/dashboard" className="inline-block mt-6 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold px-6 py-3 text-sm">Go to dashboard →</Link>
          </>
        )}
      </div>
    </div>
  );
}

const PAID_COPY = {
  one_on_one: { title: "Payment received.", body: "Sankalp's team will confirm a time within your preferred window and email you. You'll also get a reminder a day before." },
  workshop: { title: "You're enrolled.", body: "Your workshop seat is confirmed. The joining link will be emailed to you before it starts." },
};

function PaidState({ data }) {
  const copy = PAID_COPY[data?.item_kind] || { title: "You're in.", body: "Your booking is confirmed. Head to your dashboard to get started." };
  return (
    <>
      <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
        <CheckCircle2 className="w-8 h-8 text-emerald-400" />
      </div>
      <h1 data-testid="pay-success-title" className="mt-6 font-adult-heading text-3xl font-semibold tracking-tight">{copy.title}</h1>
      <div className="mt-2 text-zinc-400">{copy.body}</div>
      <Link to="/dashboard" data-testid="pay-success-dashboard" className="inline-block mt-6 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold px-6 py-3 text-sm glow-amber">Go to dashboard →</Link>
    </>
  );
}
